import * as React from "react";
import { FileDown, Loader2, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  createDDQFilledExportLink,
  type DDQFilledExportRequestFormat,
} from "@/lib/api";

/**
 * DDQ v1.5 export actions, rendered in the DDQ-response header:
 *  - "Filled template" — the LP's original .docx/.xlsx with the served
 *    answers written back in (PDF originals fall back to a clean .docx).
 *  - "Print / PDF" — a print-optimized view the LP saves as PDF from the
 *    browser (Ctrl/Cmd+P → Save as PDF).
 *
 * Both mint a single-use 5-minute link (same security profile as the
 * Markdown/JSON export-link flow) and open it in a new tab. Exports carry
 * only what the LP was already served: answers + cited ungated sources.
 */
export function DDQExportFilledButtons({ submissionId }: { submissionId: string }) {
  const [busy, setBusy] = React.useState<DDQFilledExportRequestFormat | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [note, setNote] = React.useState<string | null>(null);

  async function exportAs(format: DDQFilledExportRequestFormat) {
    setBusy(format);
    setError(null);
    setNote(null);
    try {
      const link = await createDDQFilledExportLink(submissionId, format);
      window.open(link.url, "_blank", "noopener");
      if (link.template_fallback) {
        setNote(
          "The original was a PDF, so the download is a clean .docx with your questions and answers.",
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy !== null}
        onClick={() => void exportAs("template")}
        aria-label="Download your original DDQ with answers filled in"
        title="Download your original DDQ file with the answers written back in"
      >
        {busy === "template" ? <Loader2 className="animate-spin" /> : <FileDown />}
        Filled template
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy !== null}
        onClick={() => void exportAs("print")}
        aria-label="Open a print-optimized view to save as PDF"
        title="Open a print-optimized view — save it as PDF from your browser"
      >
        {busy === "print" ? <Loader2 className="animate-spin" /> : <Printer />}
        Print / PDF
      </Button>
      {error ? (
        <span className="text-xs text-destructive" role="alert">
          {error}
        </span>
      ) : null}
      {note ? <span className="text-xs text-muted-foreground">{note}</span> : null}
    </div>
  );
}
